import { ReactNode, useState } from 'react';
import { useValidateMobileCheck } from '@frx/frx-react-context';
import camelCase from 'lodash/fp/camelCase';
import startCase from 'lodash/fp/startCase';
import toLower from 'lodash/fp/toLower';
import { useToast } from '@chakra-ui/react';
import {
  MobileCheckDepositContext,
  StepsProps,
} from './MobileCheckDepositContext';

const autoCaptureHints = {
  MITEK_ERROR_GLARE: 'Reduce Glare',
  MITEK_ERROR_FOUR_CORNER: 'Document Not Found',
  MITEK_ERROR_TOO_DARK: 'Too Dark. Use good lighting',
  MITEK_ERROR_FOCUS: 'Hold Steady',
  MITEK_ERROR_TOO_FAR: 'Document Too Far',
  MITEK_ERROR_TOO_CLOSE: 'Document Too Close',
  MITEK_ERROR_NOT_CENTERED: 'Document Not Centered',
  MITEK_ERROR_MIN_PADDING: 'Move further away',
  MITEK_ERROR_HORIZONTAL_FILL: 'Move closer',
  MITEK_ERROR_LOW_CONTRAST: 'Center document on a dark background',
  MITEK_ERROR_BUSY_BACKGROUND: 'Center document on a plain background',
  MITEK_ERROR_SKEW_ANGLE: 'Reduce angle',
};

const addCaptureButton = async () => {
  const miteKSDK = await import('../../../mitek-science-sdk');
  const mitekDisplayContainer = document.querySelector(
    '#mitekDisplayContainer'
  );
  // add a button to allow the user to capture a frame
  const buttonEl = document.createElement('button');
  buttonEl.setAttribute('id', 'mitekCaptureButtton');
  buttonEl.innerHTML = 'Capture';
  buttonEl.onclick = () => {
    miteKSDK.cmd('CAPTURE_FRAME');
  };
  mitekDisplayContainer?.appendChild(buttonEl);
};

const addCancelButton = async () => {
  const miteKSDK = await import('../../../mitek-science-sdk');
  const mitekDisplayContainer = document.querySelector(
    '#mitekDisplayContainer'
  );
  // add a button to allow the user to capture a frame
  const buttonEl = document.createElement('button');
  buttonEl.setAttribute('id', 'mitekCancelButtton');
  buttonEl.innerHTML = 'Cancel';
  buttonEl.onclick = () => {
    miteKSDK.cmd('SDK_STOP');
  };
  mitekDisplayContainer?.appendChild(buttonEl);
};

export type MobileCheckDepositProviderProps = {
  children?: ReactNode;
};

export const MobileCheckDepositProvider = ({
  children,
}: MobileCheckDepositProviderProps): JSX.Element => {
  const [steps, setSteps] = useState<StepsProps>({
    activeStep: 0,
    prevSteps: [],
  });
  const [formData, setFormData] = useState({});
  const toast = useToast();

  const sendDonations = useValidateMobileCheck();

  const handleNextStep = () => {
    setSteps((prevState) => ({
      activeStep: prevState.activeStep + 1,
      prevSteps: prevState.prevSteps.length
        ? [...prevState.prevSteps, prevState.activeStep]
        : [prevState.activeStep],
    }));
  };

  const handleStep = (stepId: number) =>
    setSteps((prevState) => ({
      activeStep: stepId,
      prevSteps: [...prevState.prevSteps, stepId],
    }));

  const handleFormData = (data: any) =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    setFormData((prevState) => ({ ...prevState, ...data }));

  const handleCapture = async (docType = '') => {
    const miteKSDK = await import('../../../mitek-science-sdk');
    const formatCheckType = startCase(toLower(docType));
    // start an auto-capture session
    miteKSDK.on('CAMERA_DISPLAY_STARTED', () => {
      miteKSDK.cmd(
        'SHOW_HINT',
        `Center ${toLower(formatCheckType)} on a dark background`
      );
    });

    miteKSDK.on('FRAME_PROCESSING_FEEDBACK', (result: any) => {
      miteKSDK.cmd('SHOW_HINT', (autoCaptureHints as any)[result.key]);
    });

    miteKSDK.on('FRAME_PROCESSING_STARTED', () => {
      addCaptureButton();
      addCancelButton();
    });

    miteKSDK.on('FRAME_CAPTURE_RESULT', (result: any) => {
      handleFormData({
        [`${camelCase(formatCheckType)}Image`]: result?.response,
      });
      toast({
        title: `${formatCheckType} Captured`,
        status: 'success',
        duration: 1000,
      });
      handleNextStep();
    });

    miteKSDK.on('SDK_ERROR', (err: any) => {
      console.log(err);
    });

    miteKSDK.cmd('CAPTURE_AND_PROCESS_FRAME', {
      mode: 'AUTO_CAPTURE',
      documentType: docType,
      mitekSDKPath: '/frx-programs/frx-fundraising-center/mitekSDK',
      options: {
        qualityPercent: 80, // value between 50 to 100 recommended
        hintFrequencyMS: 1200, // how long the hint message displays ( in MS) / note: 2400 recommended for screen readers
        hintAriaLive: 2, // screen reader setting for hint updates value  (0 = off / 1 = polite / 2 = assertive)
        guidePaddingLevel: 1, // size of the overlay guide (1=large,  2 = medium, 3 = small) /note: 1 is recommended
        hintMessageSize: 1, // set the size of the hint message  (1 = small / 2 = medium / 3 = large)
        disableSmileDetection: true, // disable smile detection
        // videoContainerId: "videoContainer", // (optionally) embed the video in a custom HTML container
        videoRecordingEnabled: false, // (optionally) record the video session / returned as a dataURL
        audioRecordingEnabled: false, // (optionally) also record audio/ only applies if videoRecordingEnabled = true)
        videoQuality: 70, // (optionally) value between 30 to 100 recommended(normalized bitrate of video / only applies if videoRecordingEnabled = true)
        videoRecordingMessage: 'Recording', // (optionally) override the default  "Recording" message / Note: set to "" to disable icon and message
      },
    });
  };

  return (
    <MobileCheckDepositContext.Provider
      /* eslint-disable-next-line react/jsx-no-constructed-context-values */
      value={{
        data: { formData, steps },
        commands: {
          handleNextStep,
          handleFormData,
          handleCapture,
          handleStep,
          sendDonations,
        },
      }}
    >
      {children}
    </MobileCheckDepositContext.Provider>
  );
};
